import PropTypes from 'prop-types';
import { DeleteIcon, PlusIcon, SuccessCircle } from '../../assets/icons';
import Spinner from '../common/Spinner';
import useModalClose from '../../hooks/useModalClose';
import { getAllUsers, updateAssignment, getAllAssignments } from '../../services/vehicleService';
import { useEffect, useState, useMemo } from 'react';
import { MultiSelect } from 'primereact/multiselect';
import { getDistricts, getNeighborhoods } from '../../services/addressService';

const EditAssignmentModal = ({ isOpen, onClose, onSuccess, assignmentId }) => {
  // Use useMemo to maintain the initial state across re-renders
  const initialState = useMemo(() => ({
    isLoading: false,
    error: null,
    formData: {
      numberPlate: '',
      district: '',
      districtId: '',
      neighbourHood: '',
      assignmentDate: '',
      users: []
    },
    districts: [],
    neighborhoods: [],
    storedLocations: [],
    users: [],
    selectedUsers: [],
    tempSelectedUsers: [],
    originalUsers: [],
    isLoadingDistricts: false,
    isLoadingNeighborhoods: false,
    isLoadingUsers: false,
    isEditingLocation: false,
    isEditingUsers: false,
    isSuccess: false,
    isLoadingAssignment: false,
    isValid: false
  }), []);

  const [state, setState] = useState(initialState);

  // Helper function to update state
  const updateState = (updates) => {
    setState(current => ({
      ...current,
      ...updates
    }));
  };

  useModalClose(isOpen, onClose);

  // Reset state ONLY when modal is explicitly closed
  useEffect(() => {
    if (!isOpen) {
      const timeout = setTimeout(() => {
        setState(initialState);
      }, 200);
      return () => clearTimeout(timeout);
    }
  }, [isOpen, initialState]);

  // Fetch assignment data when modal opens
  useEffect(() => {
    if (isOpen && assignmentId) {
      fetchAssignmentData();
    }
  }, [isOpen, assignmentId]);

  const fetchAssignmentData = async () => {
    try {
      updateState({ isLoadingAssignment: true, error: null });

      const data = await getAllAssignments({ id: assignmentId });

      if (data.status === 200 && data.assignments && data.assignments.length > 0) {
        const assignment = data.assignments[0];
        const userIds = assignment.users.map(user => user.id);
        
        updateState({
          formData: {
            numberPlate: assignment.numberPlate,
            district: assignment.district,
            districtId: '',
            neighbourHood: assignment.neighbourHood,
            assignmentDate: assignment.assignmentDate.split('T')[0],
            users: assignment.users
          },
          selectedUsers: userIds,
          originalUsers: userIds,
          storedLocations: [{
            districtName: assignment.district,
            neighborhoodName: assignment.neighbourHood
          }]
        });
      } else {
        updateState({ error: 'Görevlendirme bilgileri alınamadı.' });
      }
    } catch (error) {
      console.error('Failed to fetch assignment:', error);
      updateState({ error: 'Görevlendirme bilgileri alınamadı.' });
    } finally {
      updateState({ isLoadingAssignment: false });
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchDistricts();
      fetchUsers();
    }
  }, [isOpen]);

  useEffect(() => {
    if (state.formData.districtId) {
      fetchNeighborhoods(state.formData.districtId);
    } else {
      updateState({ neighborhoods: [] });
    }
  }, [state.formData.districtId]);

  const fetchDistricts = async () => {
    try {
      updateState({ isLoadingDistricts: true });
      const response = await getDistricts();
      const data = await response;
      if (data.status === 200) {
        updateState({ districts: data.data || [] });
      } else {
        updateState({ 
          districts: [],
          error: 'İlçeler yüklenirken bir hata oluştu.'
        });
      }
    } catch (error) {
      console.error('Failed to fetch districts:', error);
      updateState({ 
        districts: [],
        error: 'İlçeler yüklenirken bir hata oluştu.'
      });
    } finally {
      updateState({ isLoadingDistricts: false });
    }
  };

  const fetchNeighborhoods = async (districtId) => {
    try {
      updateState({ isLoadingNeighborhoods: true });
      const response = await getNeighborhoods(districtId);
      const data = await response;
      if (data.status === 200) {
        updateState({ neighborhoods: data.data || [] });
      } else {
        updateState({ 
          neighborhoods: [],
          error: 'Mahalleler yüklenirken bir hata oluştu.'
        });
      }
    } catch (error) {
      console.error('Failed to fetch neighborhoods:', error);
      updateState({ 
        neighborhoods: [],
        error: 'Mahalleler yüklenirken bir hata oluştu.'
      });
    } finally {
      updateState({ isLoadingNeighborhoods: false });
    }
  };

  const fetchUsers = async () => {
    try {
      updateState({ isLoadingUsers: true });
      const response = await getAllUsers();
      if (response.status === 200) {
        updateState({ users: response.users || [] });
      } else {
        updateState({ 
          users: [],
          error: 'Kullanıcılar yüklenirken bir hata oluştu.'
        });
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
      updateState({ 
        users: [],
        error: 'Kullanıcılar yüklenirken bir hata oluştu.'
      });
    } finally {
      updateState({ isLoadingUsers: false });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'districtId') {
      const selectedDistrict = state.districts.find(d => d.id === value);
      updateState({
        formData: {
          ...state.formData,
          district: selectedDistrict ? selectedDistrict.ad : '',
          districtId: value,
          neighbourHood: ''
        }
      });
    } else {
      updateState({
        formData: {
          ...state.formData,
          [name]: value
        }
      });
    }
  };

  const handleAddLocation = () => {
    if (!state.formData.districtId || !state.formData.neighbourHood) {
      updateState({ error: 'Lütfen ilçe ve mahalle seçiniz' });
      return;
    }

    const selectedDistrict = state.districts.find(d => d.id === state.formData.districtId);
    const selectedNeighborhood = state.neighborhoods.find(n => n.ad === state.formData.neighbourHood);

    if (!selectedDistrict || !selectedNeighborhood) {
      updateState({ error: 'Geçersiz ilçe veya mahalle seçimi' });
      return;
    }

    const newLocation = {
      districtId: selectedDistrict.id,
      districtName: selectedDistrict.ad,
      neighborhoodId: selectedNeighborhood.id,
      neighborhoodName: selectedNeighborhood.ad
    };

    updateState({
      storedLocations: [...state.storedLocations, newLocation],
      error: '',
      formData: {
        ...state.formData,
        districtId: '',
        neighbourHood: ''
      },
      isEditingLocation: false
    });
  };

  const handleUserSelectionChange = (e) => {
    updateState({ tempSelectedUsers: e.value });
  };

  const handleAddUsers = () => {
    if (state.tempSelectedUsers.length === 0) {
      updateState({ error: 'En az bir kişi seçilmelidir' });
      return;
    }

    updateState({ 
      selectedUsers: state.tempSelectedUsers,
      isEditingUsers: false,
      error: ''
    });
  };

  const handleDeleteLocation = (index) => {
    updateState({
      storedLocations: state.storedLocations.filter((_, i) => i !== index)
    });
  };

  // Validate form data
  useEffect(() => {
    // Check date is selected and valid
    const selectedDate = new Date(state.formData.assignmentDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const isDateValid = state.formData.assignmentDate && selectedDate >= today;
    
    // Check plate number
    const isPlateValid = state.formData.numberPlate.trim().length > 0;
    
    // Check location
    const isLocationValid = state.storedLocations.length === 1;
    
    // Check assignees
    const hasAssignees = state.selectedUsers.length > 0;

    // Set appropriate error message
    let errorMessage = null;
    if (!isDateValid) {
      errorMessage = 'Tarih seçilmelidir';
    } else if (!isPlateValid) {
      errorMessage = 'Plaka numarası girilmelidir';
    } else if (!isLocationValid) {
      errorMessage = 'Bir görevlendirme yeri seçilmelidir';
    } else if (!hasAssignees) {
      errorMessage = 'En az bir kişi seçilmelidir';
    }

    // Update validity state
    updateState({
      isValid: isDateValid && isPlateValid && isLocationValid && hasAssignees,
      error: errorMessage
    });
  }, [state.formData.assignmentDate, state.formData.numberPlate, state.storedLocations, state.selectedUsers]);

  const handleSubmit = async () => {
    if (!state.isValid) return;

    updateState({ isLoading: true, error: null });

    try {
      const location = state.storedLocations[0];
      const updatedData = {
        ...state.formData,
        district: location.districtName,
        neighbourHood: location.neighborhoodName,
        users: state.selectedUsers
      };

      const result = await updateAssignment(assignmentId, updatedData);
      
      // Create updated assignment data to pass back to parent
      const updatedAssignment = {
        ...updatedData,
        assigmentId: assignmentId
      };
      
      updateState({ isSuccess: true });
      
      // Notify parent component with a slight delay to ensure our state updates first
      setTimeout(() => {
        onSuccess(updatedAssignment);
      }, 100);
    } catch (error) {
      console.error('Failed to update assignment:', error);
      updateState({ 
        error: error.message || 'Görev düzenlenirken bir hata oluştu.',
        isLoading: false
      });
    } finally {
      updateState({ isLoading: false });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content edit-assignment-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Görevlendirmeyi Düzenle</h3>
        </div>

        {state.isLoading || state.isLoadingAssignment ? (
          <div className="loading-state">
            <Spinner size="large" />
            <h3 style={{color: '#18964e'}}>{state.isLoadingAssignment ? 'Görevlendirme bilgileri yükleniyor...' : 'Süreciniz tamamlanıyor...'}</h3>
          </div>
        ) : state.isSuccess ? (
          <div className="success-state">
            <SuccessCircle />
            <h3 style={{color: '#18964e'}}>Görevlendirme düzenlemeniz başarıyla yapılmıştır.</h3>
          </div>
        ) : (
          <div className="modal-body">
            <div className="form-group">
              <div className="date-input-container">
                <input
                  type="date"
                  id="assignmentDate"
                  name="assignmentDate"
                  value={state.formData.assignmentDate}
                  onChange={handleInputChange}
                />
              </div>
              <div className="input-container">
                <input
                  type="text"
                  id="numberPlate"
                  name="numberPlate"
                  value={state.formData.numberPlate}
                  maxLength={10}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="stored-locations">
              <h3 className='form-title'>Eklenen Görevlendirme Yeri</h3>
              {state.storedLocations.map((location, index) => (
                <div key={index} className="location-row">
                  <span>{location.districtName}</span>
                  <span>{location.neighborhoodName} Mahallesi</span>
                  <button 
                      type="button" 
                      className="delete-location-button"
                      onClick={() => handleDeleteLocation(index)}
                    >
                      <DeleteIcon color='#2A353D' />
                    </button>
                </div>
              ))}
              {!state.isEditingLocation && (
                <button 
                  type="button" 
                  className="edit-location-button"
                  onClick={() => updateState({ isEditingLocation: true })}
                >
                  <p>Görevlendirme Yerini Düzenle</p>
                  <PlusIcon color='#18964e' />
                </button>
              )}
              {state.isEditingLocation && (
                 <>
                    <h3 className='form-title'>Yeni Görevlendirme Yeri Bilgileri</h3>
                    <div className="form-group">
                      <div className="select-container">
                          <select
                            name="districtId"
                            value={state.formData.districtId}
                            onChange={handleInputChange}
                            className={state.formData.districtId ? 'has-value' : ''}
                          >
                            <option value="">İlçe seçiniz</option>
                            {state.districts.map(district => (
                              <option key={district.id} value={district.id}>
                                {district.ad}
                              </option>
                            ))}
                          </select>
                      </div>
                    </div>
                    <div className="form-group">
                      <div className="select-container">
                        <select
                          name="neighbourHood"
                          value={state.formData.neighbourHood}
                          onChange={handleInputChange}
                          disabled={!state.formData.districtId}
                          className={state.formData.neighbourHood ? 'has-value' : ''}
                        >
                          <option value="">Mahalle seçiniz</option>
                          {state.neighborhoods.map(neighborhood => (
                            <option key={neighborhood.id} value={neighborhood.ad}>
                              {neighborhood.ad}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <button 
                      type="button" 
                      className="add-location-button"
                      onClick={handleAddLocation}
                    >
                      Görevlendirme Yeri Ekle
                    </button>
                    <div className='edit-location-warning-text'>
                        <p>Görevlendirme ekle butonuna bastığınızda silmek istediğiniz<br/><span>görevlendirme yerini silmeden düzenleme adımı tamamlanamaz.</span></p>
                    </div>
                </>
              )} 
            </div>

            <div className="user-selection">
              <h3 className="form-title">Eklenen Kişiler</h3>
              <div className='selected-users-container'>
                {(state.isEditingUsers ? state.tempSelectedUsers : state.selectedUsers).map(userId => {
                  const user = state.users.find(u => u.id === userId);
                  if (!user) return null;
                  const isNewlySelected = state.isEditingUsers && !state.selectedUsers.includes(userId);
                  return (
                    <div key={userId} className={`selected-user ${isNewlySelected ? 'newly-selected' : ''}`}>
                      <p>{user.ad}</p>
                      <p>{user.tc}</p>
                      <button
                        type="button"
                        onClick={() => {
                          if (state.isEditingUsers) {
                            updateState({
                              tempSelectedUsers: state.tempSelectedUsers.filter(id => id !== userId)
                            });
                          } else {
                            updateState({
                              selectedUsers: state.selectedUsers.filter(id => id !== userId)
                            });
                          }
                        }}
                      >
                        <DeleteIcon color="#2A353D" />
                      </button>
                    </div>
                  );
                })}
              </div>
              {!state.isEditingUsers && (
                <button 
                  type="button" 
                  className="edit-location-button"
                  onClick={() => {
                    updateState({
                      isEditingUsers: true,
                      tempSelectedUsers: [...state.selectedUsers]
                    });
                  }}
                >
                  <p>Yeni Kişi Ekle</p>
                  <PlusIcon color='#18964e' />
                </button>
              )}
              {state.isEditingUsers && (
                <>
                  <h3 className="form-title">Kişileri Ekle</h3>
                  <MultiSelect 
                    value={state.tempSelectedUsers} 
                    onChange={handleUserSelectionChange}
                    options={state.users.map(user => ({
                      label: `${user.ad}`,
                      value: user.id
                    }))} 
                    optionLabel="label"
                    showSelectedLabels={true}
                    placeholder={<div>Kişi seçiniz. <span className="required">(Çoktan seçmeli, zorunlu)</span></div>}
                    className='multi-select-container'
                    filter
                    filterPlaceholder='Arama Yap'
                    hideOnClickOutside={true}
                  />
                  <button 
                    type="button" 
                    className="add-location-button"
                    onClick={handleAddUsers}
                    style={{marginTop: '45px'}}
                  >
                    Kişi/Kişileri Ekle
                  </button>
                </>
              )}
            </div>

            {!state.isValid && state.error && <div className="error-message">{state.error}</div>}

            <div className="form-actions">
              <button 
                type="button" 
                onClick={handleSubmit}
                disabled={!state.isValid}
                className={`submit-button ${state.isValid ? 'valid' : ''}`}
              >
                Düzenlemeleri Kaydet
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

EditAssignmentModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  assignmentId: PropTypes.string
};

export default EditAssignmentModal; 